import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { overtimeApprovalService } from '@/api/domains/overtime/overtimeApproval.service';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { useUI } from '@/hooks/ui/useUI';
import { useTranslation } from '@/i18n';
import { ROUTE_DEFINITIONS } from '@/routes';
import type { ApiResponse } from '@/api/shared';
import type { OvertimeApprovalRow } from '@/api/domains/overtime/overtimeApproval.types';
import type { BannerPart } from '@/components/ui/banners';
import type { BulkActionItem } from '@/components/ui/tables/Table.types';

/** Tope de colaboradores por lote (aprobar o reversar); debe coincidir con el validador backend. */
const MAX_BULK_EMPLOYEES = 100;

interface BulkOperationTotals {
  totalResolved: number;
  totalAlreadyResolved: number;
  totalNotProcessed: number;
}

/** Textos de la operación, ya traducidos: uno por cada desenlace posible del lote. */
interface BulkOperationMessages {
  success: string;
  summarySuccess: (count: number) => string;
  summarySkipped: (count: number) => string;
  summaryNotProcessed: (count: number) => string;
  /** Filas omitidas localmente por su estado (p. ej. Pendiente al reversar, o Aprobada al
   *  aprobar): ambas acciones pueden descartar parte de la selección desde ASIS-5354, que
   *  amplió la selección a cualquier estado. */
  summaryIneligible: (count: number) => string;
  error: string;
}

type BulkMessagePrefix = 'bulkApprove' | 'bulkReverse' | 'bulkJustify';

/**
 * Cómo se relaciona el lote con la selección de checkboxes. Es explícito porque no toda operación
 * nace de la selección: la justificación unitaria del menú de fila opera sobre UNA fila que puede
 * no estar marcada, y ahí ni el conteo de omitidas ni la limpieza de checkboxes deben referirse a
 * una selección que no participó (ASIS-5358).
 */
interface BulkOperationOptions {
  /** Universo contra el que se calculan las filas omitidas localmente por su estado. */
  selectionBaseCount: number;
  /** Si al terminar debe vaciarse la selección de la tabla. */
  clearSelection: boolean;
}

export interface UseOvertimeApprovalBulkActionsParams {
  rows: OvertimeApprovalRow[];
  monthlyCutId: number | null;
  isMonthlyCutClosed: boolean;
  refetch: () => void;
  /** Identidad que cambia cuando debe limpiarse la selección vigente (nuevo universo). */
  filtersSnapshot: unknown;
  debouncedSearchText: string;
  /**
   * Perfil efectivo del usuario en el flujo REVISOR -> APROBADOR (ASIS-5358). Decide qué acciones
   * masivas se ofrecen: el revisor ve Justificar en lugar de Aprobar y Reversar, que pertenecen a
   * la otra mitad del flujo y que además el backend le rechaza con 403.
   */
  isReviewerView?: boolean;
  /** Abre el panel lateral de justificación con los colaboradores seleccionados. */
  onOpenJustifyPanel?: (rows: OvertimeApprovalRow[], options: { fromSelection: boolean }) => void;
}

export interface UseOvertimeApprovalBulkActionsResult {
  isSelectable: boolean;
  bulkActions: BulkActionItem[];
  onSelectionChange: (selected: OvertimeApprovalRow[]) => void;
  isRowSelectable: (row: OvertimeApprovalRow) => boolean;
  selectedRowIds: Set<string | number>;
  closedCutMessage: BannerPart[] | null;
  /** Filas seleccionadas, para que el panel de justificación sepa sobre quién opera (ASIS-5358). */
  selectedRows: OvertimeApprovalRow[];
  /**
   * Ejecuta la justificación del revisor sobre las filas indicadas y resuelve en `true` solo si el
   * backend respondió correctamente, para que quien la invoque decida si cierra su formulario.
   * Recibe las filas de forma explícita porque la acción unitaria del menú opera sobre una fila que
   * no necesariamente está en la selección de la tabla; `fromSelection` distingue ese caso, en el
   * que la selección vigente ni se cuenta como omitida ni se limpia.
   */
  justifySelection: (
    targetRows: OvertimeApprovalRow[],
    justificationTypeId: number,
    freeComment: string | undefined,
    options?: { fromSelection?: boolean },
  ) => Promise<boolean>;
}

/**
 * Selección y acciones masivas (aprobar / reversar) de horas extras (ASIS-5353, ASIS-5354).
 * Extraído de useOvertimeApproval para no dejar ese hook monolítico — es autocontenido: solo
 * depende de las filas visibles, el corte vigente y un refetch, sin acoplarse a filtros avanzados
 * ni a la carga de cortes.
 */
export function useOvertimeApprovalBulkActions(
  params: UseOvertimeApprovalBulkActionsParams,
): UseOvertimeApprovalBulkActionsResult {
  const {
    rows, monthlyCutId, isMonthlyCutClosed, refetch, filtersSnapshot, debouncedSearchText,
    isReviewerView = false, onOpenJustifyPanel,
  } = params;

  const { t: translate } = useTranslation();
  const { showApiError, showSuccess, showWarning, openModal } = useUI();
  const { can } = usePagePermissions();
  const navigate = useNavigate();

  // Selección para las acciones masivas. El hook es la única fuente de verdad: la tabla recibe
  // `selectedRowIds` en modo controlado, de modo que cualquier corrección hecha aquí (tope por
  // lote, limpieza tras operar o al cambiar de universo) se refleja en los checkboxes.
  const [selectedRows, setSelectedRows] = useState<OvertimeApprovalRow[]>([]);
  // Ref (no estado) para que la guarda de reentrada lea el valor vigente de forma síncrona: un
  // flag de estado seguiría en false si la acción se dispara dos veces antes del re-render.
  const isSubmittingBulkRef = useRef(false);

  // Un corte cerrado no admite modificaciones: el aviso queda visible mientras dure la selección
  // de ese corte y ninguna acción masiva se ofrece. Se entrega en segmentos porque el Banner
  // resalta el prefijo "Atención:", igual que el resto de avisos de bloqueo del producto.
  const closedCutMessage = useMemo<BannerPart[] | null>(
    () => (isMonthlyCutClosed
      ? [
        { text: translate('attendance:overtimeApproval.messages.closedCutEmphasis'), emphasis: true },
        { text: translate('attendance:overtimeApproval.messages.closedCut'), emphasis: false },
      ]
      : null),
    [isMonthlyCutClosed, translate],
  );

  // Cualquier cambio de universo invalida la selección: los colaboradores marcados ya no están a la
  // vista y operar sobre ellos sería hacerlo a ciegas. Aplica al corte, a los filtros principales y
  // avanzados (`filtersSnapshot`) y a la búsqueda de la grilla. La paginación NO limpia: ahí el
  // universo es el mismo y la selección entre páginas es deliberada.
  useEffect(() => {
    setSelectedRows([]);
  }, [filtersSnapshot, debouncedSearchText]);

  // Toda fila es seleccionable, sin importar su estado ni el permiso del usuario ni si el corte
  // está cerrado (ASIS-5355): el permiso mínimo para ver la pantalla ya basta para seleccionar y
  // usar la acción "Detalle" (solo navegación). Aprobar y Reversar siguen exigiendo su propia
  // habilitación — can('edit') && !isMonthlyCutClosed — más abajo, en `bulkActions`.
  const isRowSelectable = useCallback(() => true, []);

  const isSelectable = true;

  // Habilitación real de las acciones que mutan datos (Aprobar / Reversar) — Detalle queda fuera
  // porque es solo navegación y no depende de ningún permiso.
  const canMutate = can('edit') && !isMonthlyCutClosed;

  // Selección entregada a la tabla en modo controlado: lo que decida este hook (tope por lote,
  // limpieza tras operar o al cambiar de universo) es lo que muestran los checkboxes.
  const selectedRowIds = useMemo<Set<string | number>>(
    () => new Set(selectedRows.map((row) => row.id)),
    [selectedRows],
  );

  // La tabla emite únicamente las filas seleccionadas de la página visible: se reemplaza ese
  // subconjunto y se conserva lo seleccionado en otras páginas, para que marcar y desmarcar
  // funcionen entre páginas. Si el conjunto supera el tope se recorta conservando lo anterior.
  const onSelectionChange = useCallback((selected: OvertimeApprovalRow[]) => {
    const currentPageIds = new Set(rows.map((row) => row.id));
    const fromOtherPages = selectedRows.filter((row) => !currentPageIds.has(row.id));
    const merged = [...fromOtherPages, ...selected];

    if (merged.length > MAX_BULK_EMPLOYEES) {
      showWarning(translate('attendance:overtimeApproval.bulkSelection.limitReached', {
        max: MAX_BULK_EMPLOYEES,
      }));
      setSelectedRows(merged.slice(0, MAX_BULK_EMPLOYEES));
      return;
    }

    setSelectedRows(merged);
  }, [rows, selectedRows, showWarning, translate]);

  // Subconjuntos elegibles derivados de la selección vigente: Aprobar excluye lo ya Aprobada,
  // Reversar excluye lo Pendiente (sin horas aprobadas que reversar) — exactamente el criterio del
  // ticket ("registros en estado Aprobada o Parcial").
  const approvableRows = useMemo(
    () => selectedRows.filter((row) => row.status !== 'Aprobada'),
    [selectedRows],
  );

  const reversableRows = useMemo(
    () => selectedRows.filter((row) => row.status !== 'Pendiente'),
    [selectedRows],
  );

  // Un único bloque de textos por prefijo ('bulkApprove'/'bulkReverse'): aprobar y reversar
  // comparten la misma forma de mensajes (éxito, resumen, ya resuelto, no procesado, omitido,
  // error), así que evita repetir seis llamadas a translate por cada acción. summarySkipped viaja
  // desde bulkSelection porque su texto es idéntico entre ambas acciones.
  const buildBulkMessages = useCallback((prefix: BulkMessagePrefix): BulkOperationMessages => ({
    success: translate(`attendance:overtimeApproval.${prefix}.success`),
    summarySuccess: (count) => translate(`attendance:overtimeApproval.${prefix}.summarySuccess`, { count }),
    // El motivo del descarte difiere por acción: para aprobar/reversar es "ya resuelta por otro
    // usuario"; para justificar es "sus horas ya fueron aprobadas", que es la etapa siguiente del
    // flujo y no un conflicto entre pares.
    summarySkipped: (count) => translate(
      prefix === 'bulkJustify'
        ? 'attendance:overtimeApproval.bulkSelection.summarySkippedJustify'
        : 'attendance:overtimeApproval.bulkSelection.summarySkipped',
      { count },
    ),
    summaryNotProcessed: (count) => translate(`attendance:overtimeApproval.${prefix}.summaryNotProcessed`, { count }),
    summaryIneligible: (count) => translate(`attendance:overtimeApproval.${prefix}.summaryIneligible`, { count }),
    error: translate(`attendance:overtimeApproval.${prefix}.error`),
  }), [translate]);

  // Núcleo común a aprobar/reversar: guardas de reentrada y permiso, llamada al servicio, desglose
  // de mensajes por desenlace, limpieza de selección y refresco de la grilla. `eligibleRows` ya
  // viene filtrado por la acción concreta; lo omitido localmente (p. ej. filas Pendiente al
  // reversar, o Aprobada al aprobar) se informa aparte porque es un motivo distinto de "ya resuelto
  // por otro usuario".
  const runBulkOperation = useCallback(async (
    eligibleRows: OvertimeApprovalRow[],
    call: () => Promise<ApiResponse<BulkOperationTotals>>,
    messages: BulkOperationMessages,
    options: BulkOperationOptions,
  ): Promise<boolean> => {
    // Defensa en profundidad (Regla 12): bulkActions/isSelectable ya derivan de can('edit') y
    // !isMonthlyCutClosed, pero la mutación no debe disparar si ese estado se pierde en algún
    // punto intermedio.
    if (!can('edit') || isMonthlyCutClosed) return false;
    if (isSubmittingBulkRef.current || eligibleRows.length === 0) return false;
    if (monthlyCutId === null) return false;

    isSubmittingBulkRef.current = true;

    try {
      const response = await call();

      if (!response.success || !response.data) {
        showApiError(response, messages.error);
        return false;
      }

      const { totalResolved, totalAlreadyResolved, totalNotProcessed } = response.data;
      const ineligibleCount = options.selectionBaseCount - eligibleRows.length;
      const hasDiscarded = totalAlreadyResolved > 0 || totalNotProcessed > 0 || ineligibleCount > 0;

      // Éxito limpio solo si todo lo seleccionado quedó resuelto. Si hubo descartes se informa cada
      // motivo por separado: "ya resuelto por otro usuario", "no se pudo procesar" y "omitido por
      // su estado" son situaciones distintas y el aviso genérico llevaba a diagnósticos
      // equivocados.
      if (!hasDiscarded) {
        showSuccess(messages.success);
      } else if (totalResolved > 0) {
        showSuccess(messages.summarySuccess(totalResolved));
      }

      if (totalAlreadyResolved > 0) {
        showWarning(messages.summarySkipped(totalAlreadyResolved));
      }

      if (totalNotProcessed > 0) {
        showWarning(messages.summaryNotProcessed(totalNotProcessed));
      }

      if (ineligibleCount > 0) {
        showWarning(messages.summaryIneligible(ineligibleCount));
      }

      if (options.clearSelection) setSelectedRows([]);
      refetch();

      return true;
    } finally {
      isSubmittingBulkRef.current = false;
    }
  }, [
    can, isMonthlyCutClosed, monthlyCutId,
    showApiError, showSuccess, showWarning, refetch,
  ]);

  const approveSelection = useCallback(async () => {
    if (monthlyCutId === null) return;

    await runBulkOperation(
      approvableRows,
      () => overtimeApprovalService.approveBulk({
        monthlyCutId,
        employeeIds: approvableRows.map((row) => row.id).join(','),
      }).then((response) => ({
        ...response,
        data: response.data && {
          totalResolved: response.data.totalApproved,
          totalAlreadyResolved: response.data.totalAlreadyResolved,
          totalNotProcessed: response.data.totalNotProcessed,
        },
      })),
      buildBulkMessages('bulkApprove'),
      { selectionBaseCount: selectedRows.length, clearSelection: true },
    );
  }, [runBulkOperation, approvableRows, selectedRows.length, monthlyCutId, buildBulkMessages]);

  const reverseSelection = useCallback(async () => {
    if (monthlyCutId === null) return;

    await runBulkOperation(
      reversableRows,
      () => overtimeApprovalService.reverse({
        monthlyCutId,
        employeeIds: reversableRows.map((row) => row.id).join(','),
      }).then((response) => ({
        ...response,
        data: response.data && {
          totalResolved: response.data.totalReversed,
          totalAlreadyResolved: response.data.totalAlreadyResolved,
          totalNotProcessed: response.data.totalNotProcessed,
        },
      })),
      buildBulkMessages('bulkReverse'),
      { selectionBaseCount: selectedRows.length, clearSelection: true },
    );
  }, [runBulkOperation, reversableRows, selectedRows.length, monthlyCutId, buildBulkMessages]);

  // La aprobación reescribe las horas del corte para cada colaborador, así que pasa por
  // confirmación explícita con el conteo, igual que el resto de acciones masivas del producto.
  const handleBulkApprove = useCallback(() => {
    // Defensa en profundidad (Regla 12): ver comentario equivalente en runBulkOperation.
    if (!can('edit') || approvableRows.length === 0) return;

    openModal('ConfirmModal', {
      variant: 'success',
      icon: 'checkCircle',
      title: translate('attendance:overtimeApproval.bulkApprove.confirmTitle'),
      bodyText: translate('attendance:overtimeApproval.bulkApprove.confirmBody', {
        count: approvableRows.length,
      }),
      confirmText: translate('attendance:overtimeApproval.bulkApprove.action'),
      onConfirm: async () => { await approveSelection(); },
    });
  }, [can, approvableRows.length, openModal, translate, approveSelection]);

  // La reversa elimina la justificación de las horas del corte para cada colaborador, así que pasa
  // por confirmación explícita con el conteo, igual que la reversa unitaria (ASIS-5371).
  const handleBulkReverse = useCallback(() => {
    // Defensa en profundidad (Regla 12): ver comentario equivalente en runBulkOperation.
    if (!can('edit') || reversableRows.length === 0) return;

    openModal('ConfirmModal', {
      variant: 'error',
      icon: 'alert',
      title: translate('attendance:overtimeApproval.bulkReverse.confirmTitle'),
      bodyText: translate('attendance:overtimeApproval.bulkReverse.confirmBody', {
        count: reversableRows.length,
      }),
      confirmText: translate('attendance:overtimeApproval.bulkReverse.action'),
      onConfirm: async () => { await reverseSelection(); },
    });
  }, [can, reversableRows.length, openModal, translate, reverseSelection]);

  /**
   * Justificación masiva del revisor (ASIS-5358). Reutiliza `runBulkOperation` porque la forma del
   * desenlace es la misma que aprobar/reversar: resueltos, ya resueltos por la otra etapa y no
   * procesados. No hay subconjunto elegible que filtrar localmente: a diferencia de aprobar
   * —que descarta lo ya Aprobada— el revisor puede re-justificar una fila ya Justificada para
   * corregir el tipo, porque el SP reemplaza su marca previa y esa es su única vía de corrección
   * (la reversa del revisor no entra en este ticket).
   *
   * Con `fromSelection` en false —la acción unitaria del menú de fila— el lote no tiene nada que
   * ver con los checkboxes marcados: el universo omitido se mide contra las propias filas del lote
   * (siempre 0) y la selección se conserva intacta, en vez de contarla como omitida y vaciarla.
   */
  const justifySelection = useCallback(async (
    targetRows: OvertimeApprovalRow[],
    justificationTypeId: number,
    freeComment: string | undefined,
    options?: { fromSelection?: boolean },
  ): Promise<boolean> => {
    if (monthlyCutId === null || targetRows.length === 0) return false;

    const fromSelection = options?.fromSelection ?? false;

    return runBulkOperation(
      targetRows,
      () => overtimeApprovalService.justify({
        monthlyCutId,
        employeeIds: targetRows.map((row) => row.id).join(','),
        justificationTypeId,
        ...(freeComment !== undefined ? { freeComment } : {}),
      }).then((response) => ({
        ...response,
        data: response.data && {
          totalResolved: response.data.totalJustified,
          totalAlreadyResolved: response.data.totalAlreadyResolved,
          totalNotProcessed: response.data.totalNotProcessed,
        },
      })),
      buildBulkMessages('bulkJustify'),
      {
        selectionBaseCount: fromSelection ? selectedRows.length : targetRows.length,
        clearSelection: fromSelection,
      },
    );
  }, [runBulkOperation, monthlyCutId, selectedRows.length, buildBulkMessages]);

  const handleBulkJustify = useCallback(() => {
    // Defensa en profundidad (Regla 12): ver comentario equivalente en runBulkOperation.
    if (!can('edit') || selectedRows.length === 0) return;

    onOpenJustifyPanel?.(selectedRows, { fromSelection: true });
  }, [can, selectedRows, onOpenJustifyPanel]);

  // Navega al detalle de solo lectura (ASIS-5355) de todos los colaboradores seleccionados. Sin
  // confirmación: es solo navegación, no muta nada — por eso no exige can('edit') ni corte abierto.
  const handleBulkDetail = useCallback(() => {
    if (selectedRows.length === 0 || monthlyCutId === null) return;

    void navigate(
      `${ROUTE_DEFINITIONS.APROBAR_HHEE_DETALLE.path}?employeeIds=${
        selectedRows.map((row) => row.id).join(',')
      }&monthlyCutId=${monthlyCutId}`,
    );
  }, [selectedRows, monthlyCutId, navigate]);

  const bulkActions: BulkActionItem[] = useMemo(() => {
    const actions: BulkActionItem[] = [
      {
        // `rowActions.detail` no existe en el diccionario: i18next devolvía la clave cruda como
        // etiqueta del botón. Se usa la misma clave compartida que la acción por fila.
        label: translate('common:tableActions.detail'),
        icon: 'eye',
        onClick: handleBulkDetail,
        disabled: selectedRows.length === 0,
      },
    ];

    // Aprobar, Reversar y Justificar mutan datos: se ofrecen solo con permiso de edición y corte
    // abierto, exactamente igual que antes de ASIS-5355 — únicamente Detalle deja de exigirlo.
    if (!canMutate) return actions;

    // El revisor ejecuta la otra mitad del flujo: su única acción que muta es Justificar. Aprobar y
    // Reversar no se le ofrecen porque el backend se las rechaza con 403 (ASIS-5358).
    if (isReviewerView) {
      actions.push({
        label: translate('attendance:overtimeApproval.bulkJustify.action'),
        icon: 'checkCircle',
        onClick: handleBulkJustify,
        disabled: selectedRows.length === 0,
        tooltip: selectedRows.length === 0
          ? translate('attendance:overtimeApproval.bulkJustify.disabledTooltip')
          : undefined,
      });

      return actions;
    }

    actions.push(
      {
        label: translate('attendance:overtimeApproval.bulkApprove.action'),
        icon: 'checkCircle',
        onClick: handleBulkApprove,
        disabled: approvableRows.length === 0,
        tooltip: approvableRows.length === 0
          ? translate('attendance:overtimeApproval.bulkApprove.disabledTooltip')
          : undefined,
      },
      {
        label: translate('attendance:overtimeApproval.bulkReverse.action'),
        icon: 'sync',
        variant: 'danger',
        onClick: handleBulkReverse,
        disabled: reversableRows.length === 0,
        tooltip: reversableRows.length === 0
          ? translate('attendance:overtimeApproval.bulkReverse.disabledTooltip')
          : undefined,
      },
    );

    return actions;
  }, [
    canMutate, translate, handleBulkDetail, handleBulkApprove, handleBulkReverse,
    selectedRows.length, approvableRows.length, reversableRows.length,
    isReviewerView, handleBulkJustify,
  ]);

  return {
    isSelectable,
    bulkActions,
    onSelectionChange,
    isRowSelectable,
    selectedRowIds,
    closedCutMessage,
    selectedRows,
    justifySelection,
  };
}

export default useOvertimeApprovalBulkActions;
